import { slugify } from '../utils/slugify';
import { ContentEntry } from './siteContent';

export const UI_EN = {
  heroHeadline: 'Family Law Practice.',
  kanzleiTagline: '',
  kontaktButton: 'Contact',
  menuLabel: 'Menu',
  zurueck: 'Back',
  mehrErfahren: 'Learn more',
  lebenslauf: 'Curriculum Vitae',
  veroeffentlichungen: 'Publications',
  absenden: 'Submit',
  sending: 'Sending...',
  sent: 'Thank you, your message has been sent.',
  error: 'Error sending. Please try again.',
  form: {
    vorname: 'First Name',
    nachname: 'Last Name',
    email: 'Email',
    telefon: 'Phone Number',
    nachricht: 'Message',
  },
  formPrivacyLead: 'Information on how we process your data is in our',
  formPrivacyLink: 'privacy policy',

  nav: {
    start: 'Home',
    leistungen: 'Services',
    vorgehensweisen: 'Approach',
    team: 'Team',
    aktuelles: 'News',
    kontakt: 'Contact',
  },

  legal: {
    impressum: 'Legal Notice',
    datenschutz: 'Privacy Policy',
    widerruf: 'Right of withdrawal',
  },

  sections: {
    ueberUns: {
      eyebrow: 'about us',
      headline: 'Zöllner Lorenz is a Munich-based law firm specializing in family law, mediation and Cooperative Practice. We guide people through the most significant transitions of their lives — with legal precision and human understanding.',
      body: 'Our work is grounded in the conviction that good legal counsel does not escalate, but clarifies. Does not fight, but enables. Does not sever what belongs together — but protects what remains.',
    },
    methodik: {
      eyebrow: 'approach',
      headlineSans1: 'Guiding',
      headlineSans2: 'you through',
      headlineSans3: 'difficult',
      headlineAccent: 'times — in your own way.',
      body: 'Every situation is unique. We adapt to your needs. Together we find the right path.',
      servicesButton: 'Our Services',
    },
    leistungen: {
      eyebrow: 'services',
      headlineSans: 'Our',
      headlineAccent: 'Services',
      intro1: 'Family law is about far more than legal questions. It is about shaping your future.',
      intro2: 'Based in central Munich, we advise and represent you in all areas of family law — throughout Germany. Our services include:',
    },
    team: {
      eyebrow: 'team',
      headlineSans: 'Our',
      headlineAccent: 'Team',
      intro: 'We are Marlene Zöllner and Susanna Lorenz — two attorneys with the goal of making family law humane, competent and solution-oriented.',
    },
    ueberzeugung: {
      eyebrow: 'Our Conviction',
      quote: 'Change does not need losers.',
      quoteAccent: 'It needs the right companions.',
      body: 'Separation is not failure — it is a new beginning. We stand by your side — not against the other.',
    },
    aktuelles: {
      eyebrow: 'news',
      headlineSans1: 'Current',
      headlineSans2: 'Developments',
      headlineSans3: 'in',
      headlineAccent: 'Family Law',
      body: 'Insights into topics, legal developments and our perspective on a family law that connects families despite separation.',
      carouselPrev: 'Previous articles',
      carouselNext: 'More articles',
    },
    kontakt: {
      headline: 'Contact',
      intro: 'Describe your concern — we will get back to you promptly.',
    },
  },

  footer: {
    address: 'Maximiliansplatz 12a · 80333 Munich',
    telefon: 'Phone',
    email: 'Email',
    termine: 'Appointments by arrangement',
  },
};

export const METHODIK_ITEMS_EN: ContentEntry[] = [
  {
    slug: 'juristische-beratung-und-vertretung',
    title: 'Legal Advice and Representation',
    description: 'We advise you comprehensively on your rights and obligations. Out of court and in court, we represent you competently, clearly and with a view to what matters for you in the long term.',
    seoTitle: 'Legal Advice & Representation in Family Law | Zöllner Lorenz',
    seoDescription: 'Legal advice and representation in family law in Munich — competent, clear and focused on long-term solutions.',
    body: `A sound legal assessment of your situation is essential. We advise you comprehensively on your rights and obligations and show you which paths are open to you. We value transparency, so that you always know where you stand.

We always strive for a consensual solution. Where that is not possible, we enforce your rights decisively in court. Our goal remains an outcome that holds up in the long term.

Get in touch — together we will find the right path.`,
  },
  {
    slug: 'mediation',
    title: 'Mediation',
    description: 'We create space for conversations that cannot succeed alone, and guide you toward a solution that both sides can support.',
    seoTitle: 'Mediation in Family Law in Munich | Zöllner Lorenz',
    seoDescription: 'Mediation in separation and divorce in Munich — resolve conflicts without court, with professional guidance and self-determination.',
    body: `Mediation creates space for understanding. It is a voluntary, out-of-court process for resolving conflicts. The parties remain in direct dialogue and develop solutions together.

As mediators, we accompany this process impartially. We ensure structured and respectful communication, clarify misunderstandings and make the interests of both sides visible.

The goal is an agreement that suits both parties and that they can take joint responsibility for. Solutions developed together tend to be accepted longer-term and implemented more reliably than decisions imposed from outside.

Mediation is particularly suitable when contact continues after separation, for example as co-parents. An agreement reached can be made legally binding, for instance through notarization or recording in the divorce hearing.

Interested in mediation? Get in touch.`,
  },
  {
    slug: 'cooperative-practice',
    title: 'Cooperative Practice',
    description: 'Together at one table instead of against each other in court: with a respectful, solution-oriented negotiation culture, we guide you to a fair agreement.',
    seoTitle: 'Cooperative Practice in Munich | Zöllner Lorenz',
    seoDescription: 'Cooperative Practice (Collaborative Law) in Munich — reaching agreement without court, with dedicated legal counsel for each side.',
    body: `Like mediation, Cooperative Practice is an out-of-court, interest-based process for resolving conflicts by mutual agreement. Internationally, it is known as Collaborative Practice or Collaborative Law.

Unlike in mediation, each side is accompanied by its own specially trained attorney. All parties sit together at one table. If needed, further professionals such as coaches or child specialists join. Alongside legal and financial questions, personal concerns also have their place.

The process is shaped by mutual respect and a shared will to reach agreement. The attorneys of both sides do not work against each other, but together on a solution. They commit not to represent their clients in court. This underscores the clear focus on an out-of-court resolution. Only a consensual divorce may they also accompany in court.

With Cooperative Practice, a conflict can often be resolved faster and at lower cost than in court. The process combines the security of legal counsel with the wish for a fair agreement. The results are individual and tailored to the needs of everyone involved. This allows lasting peace to grow.

Further information:`,
    links: [
      { text: 'MNCP, Munich Network for Cooperative Practice', label: 'www.cooperative-praxis.de', href: 'https://www.cooperative-praxis.de' },
      { text: 'DVCP, German Association for Cooperative Practice', label: 'www.deutsche-vereinigung-cooperative-praxis.de', href: 'https://www.deutsche-vereinigung-cooperative-praxis.de' },
      { text: 'ENCP, European Network for Collaborative Practice', label: 'www.encp.eu', href: 'https://www.encp.eu' },
      { text: 'IACP, International Academy of Collaborative Professionals', label: 'www.collaborativepractice.com', href: 'https://www.collaborativepractice.com' },
    ],
  },
];

export const TEAM_INTRO_EN =
  'We are Marlene Zöllner and Susanna Lorenz — two attorneys with the goal of making family law humane, competent and solution-oriented.';

export const TEAM_MEMBERS_EN: ContentEntry[] = [
  {
    slug: 'marlene-zoellner',
    title: 'Marlene Zöllner',
    description:
      'Partner · Attorney · Specialist Attorney for Family Law · Certified Mediator · Cooperative Practice Attorney',
    email: 'zoellner@zoellner-lorenz.de',
    imageSrc: '/images/team-marlene.png',
    imagePosition: 'center center',
    seoTitle: 'Marlene Zöllner | Zöllner Lorenz',
    seoDescription:
      'Marlene Zöllner — Specialist Attorney for Family Law, Mediator and Cooperative Practice Attorney in Munich.',
    quote: 'Law with conviction. Solutions with heart.',
    cvEntries: [
      { period: '2026', text: 'Founding partner of Zöllner Lorenz' },
      { period: '2025', text: 'Specialist Attorney for Family Law' },
      { period: '2024', text: 'Cooperative Practice Attorney' },
      { period: '2023', text: 'Certified Mediator' },
      { period: 'since 2021', text: 'Attorney at a renowned Munich family law firm (Kanzlei Hartman-Hilter)' },
      { period: '2020', text: '2nd State Examination: Legal traineeship at the Higher Regional Court Düsseldorf, including placement at the Federal Ministry for Family Affairs' },
      { period: '2018', text: '1st State Examination: Law studies at Ludwig Maximilian University Munich and University of Helsinki' },
    ],
    publications: [
      'L. Bergschneider und B. Schramm (Eds.), Beck\'s Formularbuch Familienrecht, C.H. Beck, Munich, 7th edition 2026 — Chapter on Mediation.',
    ],
  },
  {
    slug: 'susanna-lorenz',
    title: 'Susanna Lorenz',
    description:
      'Partner · Attorney and former Public Prosecutor · Mediator · Cooperative Practice Attorney',
    email: 'lorenz@zoellner-lorenz.de',
    imageSrc: '/images/team-susanna.png',
    imagePosition: 'center center',
    seoTitle: 'Susanna Lorenz | Zöllner Lorenz',
    seoDescription:
      'Susanna Lorenz — Attorney, Mediator and Cooperative Practice Attorney in Munich.',
    cvEntries: [
      { period: '2026', text: 'Founding partner of Zöllner Lorenz' },
      { period: '2026', text: 'Cooperative Practice Attorney' },
      { period: '2026', text: 'Mediation training' },
      { period: '2026', text: 'Specialist course in Family Law' },
      { period: '2024–2026', text: 'Public Prosecutor (including prosecution of domestic violence)' },
      { period: '2020–2024', text: 'Attorney at leading international law firms (Freshfields and Osborne Clarke)' },
      { period: '2020', text: '2nd State Examination: Legal traineeship at the Higher Regional Court Düsseldorf' },
      { period: '2018', text: '1st State Examination: Law studies at Ludwig Maximilian University Munich and University of Lisbon' },
    ],
  },
];

export const LEISTUNGEN_ITEMS_EN: ContentEntry[] = [
  {
    slug: slugify('Ehevertrag und Scheidungsfolgenvereinbarung'),
    title: 'Prenuptial and Divorce Agreements',
    body: `Clear agreements create security — ideally before a dispute arises. A prenuptial agreement proactively governs what should apply in the event of a separation: such as asset protection, pension equalization and maintenance. If separation has already been decided, a divorce settlement agreement bundles the open points into a consensual solution.

Especially with business interests, real estate or gifts and inheritances, a tailored arrangement is often decisive.

We think ahead with you and develop an agreement that fits your life situation and holds up in an emergency. From the initial consultation through to notarization or recording in the divorce hearing. Fair for both sides and with a view to what endures.`,
  },
  {
    slug: slugify('Trennung und Scheidung'),
    title: 'Separation and Divorce',
    body: `A separation often reorganizes your entire life — emotionally, financially and legally. We guide you through all legal steps, from the separation year through the divorce petition to all related consequences. We also stand by you on a human level.

We keep in view not only the proceedings, but also what comes after. So that you and your children can start the next chapter well. This applies equally to married and unmarried couples.

Depending on the situation, different paths may be suitable: mediation, Cooperative Practice or legal counsel and representation. Together we discuss which path is right for you.`,
  },
  {
    slug: slugify('Umgang und Sorgerecht'),
    title: 'Custody and Visitation',
    body: `When parents separate, a central question often arises: How can the children be best accompanied and protected?

Custody concerns the essential decisions in a child's life. Visitation rights concern the lived time with both parents. The standard is always the child's welfare.

We focus on solutions that keep the child out of the parents' conflict — through clear, reliable arrangements. Our goal is a consensual arrangement that is fair to both parents and gives the child the stability needed to grow up carefree.`,
  },
  {
    slug: slugify('Vermögensauseinandersetzung und Zugewinnausgleich'),
    title: 'Asset Division and Equalization of Gains',
    body: `Under the statutory matrimonial property regime, the increase in assets acquired during the marriage is equalized between the spouses. This is the equalization of gains. With larger or complex assets, this quickly becomes demanding. Business interests, real estate and investments and their valuation require care and experience.

If you have agreed on a different arrangement in a prenuptial agreement, such as separation of property or a modified community of gains, we examine the validity of the contract and what this concretely means for you.

Beyond the equalization of gains, further asset questions often arise upon separation, such as dealing with jointly owned property, often the family home, or the division of joint accounts. We accompany you through these as well.

We first provide you with clarity about the asset situation, examine valuation questions and guide you to a solution that endures — consensually wherever possible.`,
  },
  {
    slug: slugify('Unternehmen in Trennung und Scheidung'),
    title: 'Businesses in Separation and Divorce',
    body: `If one spouse holds an interest in a business or practice, this affects almost every consequence of divorce. In the equalization of gains, the business itself is not divided. Its increase in value during the marriage, however, is included in the calculation.

We clarify what the interest means for the equalization of gains and for maintenance. We also show you how this can be settled in advance in a prenuptial agreement. The goal is a solution that keeps your business able to act in the long term.`,
  },
  {
    slug: slugify('Unterhalt'),
    title: 'Maintenance',
    body: `Maintenance is a central question in almost every separation. Maintenance law encompasses various areas — such as child maintenance, separation maintenance, post-marital maintenance or caretaker maintenance.

Each type of maintenance follows its own rules and standards and every situation has its own particularities. We calculate your entitlements or obligations transparently, assess them realistically and guide you to a viable solution — ideally by consensus.`,
  },
  {
    slug: slugify('Versorgungsausgleich'),
    title: 'Pension Equalization',
    body: `In pension equalization, the pension rights acquired during the marriage are divided between the spouses. What sounds technical has significant implications for retirement security.

We examine the pension rights included and explain clearly what the pension equalization means for your retirement provision.`,
  },
  {
    slug: slugify('Ehewohnung und Hausrat'),
    title: 'Marital Home and Household Items',
    body: `Who stays in the shared apartment, who moves out and how are household items divided? These questions need to be practically resolved upon separation, and they are rarely purely factual.

We clarify what arrangement makes sense for the transitional period and for the time after divorce, and guide you to a solution that is bearable for all involved — consensually where possible and with a view to what the situation requires.`,
  },
  {
    slug: slugify('Namensrecht'),
    title: 'Name Law',
    body: `With marriage, separation and divorce, name questions arise — for yourself and for your children. We explain your options and guide you through the necessary steps.`,
  },
  {
    slug: slugify('Adoption'),
    title: 'Adoption',
    body: `An adoption legally establishes a new family unit. Stepchild adoption, minor adoption and adult adoption each follow their own requirements and have different legal effects.

We explain clearly which form of adoption suits your situation and accompany you with care and consideration for all involved through the entire process.`,
  },
];

export const AKTUELLES_ITEMS_EN: ContentEntry[] = [
  {
    slug: slugify('Unternehmen in der Scheidung'),
    title: 'Businesses in Divorce',
    description: 'What business owners should consider regarding equalization of gains — and how to keep the business out of the conflict.',
  },
  {
    slug: slugify('Ehevertrag – klug statt unromantisch'),
    title: 'Prenuptial Agreements — Smart, Not Unromantic',
    description: 'When a prenuptial agreement makes sense, what it can regulate and how it provides security for both sides.',
  },
  {
    slug: slugify('Kindschaftsrechtsreform 2026'),
    title: 'Child Law Reform 2026: Will Shared Custody Become the Default?',
    description: 'The new draft legislation names all care models as equal for the first time — without giving preference to any one. What this really means for separated parents.',
  },
  {
    slug: slugify('Mediation im Familienrecht'),
    title: 'Mediation in Family Law',
    description: 'Why more and more couples are choosing the joint path instead of conflict.',
  },
  {
    slug: slugify('Cooperative Praxis'),
    title: 'Cooperative Practice',
    description: 'The cooperative path through separation — and its advantages over traditional adversarial proceedings.',
  },
];
